import type { PickListPick, ReadyToOutboundOrder } from '@/infrastructure/FulfillmentAdminClient';
import { kstDay } from '@/utils/fulfillmentTime';

/** The wireframe's four tabs — every order sits in exactly one of the three concrete ones. */
export type ReadyToOutboundTab = 'all' | 'inv' | 'mkt' | 'jit';

/** One queued order as this screen reads it; the client owns the shape. */
export type ReadyToOutboundRow = ReadyToOutboundOrder;

export interface ReadyToOutboundCounts {
  all: number;
  inv: number;
  mkt: number;
  jit: number;
}

export const MISSING = '—';

/** Marketing wins over JIT: an imported order stays in the Marketing tab whatever its lines source from. */
export const viewOf = (order: ReadyToOutboundRow): Exclude<ReadyToOutboundTab, 'all'> => {
  if (order.isMarketing) {
    return 'mkt';
  }
  return order.hasJit ? 'jit' : 'inv';
};

export const inView = (order: ReadyToOutboundRow, tab: ReadyToOutboundTab): boolean =>
  tab === 'all' || viewOf(order) === tab;

export const countsByView = (orders: ReadyToOutboundRow[]): ReadyToOutboundCounts =>
  orders.reduce<ReadyToOutboundCounts>(
    (counts, order) => ({ ...counts, all: counts.all + 1, [viewOf(order)]: counts[viewOf(order)] + 1 }),
    { all: 0, inv: 0, mkt: 0, jit: 0 },
  );

/** [BR-2] the picker walks units, not rows — a 2-pack of one sku is two items to pull off the shelf. */
export const unitsOf = (order: ReadyToOutboundRow): number => order.lines.reduce((units, line) => units + line.qty, 0);

export const selectedUnits = (orders: ReadyToOutboundRow[], selected: number[]): number =>
  orders.filter((order) => selected.includes(order.orderId)).reduce((units, order) => units + unitsOf(order), 0);

/** The warehouse reads Seoul dates, so the cell never shifts with the operator's own clock. */
export const formatOrderDate = (iso?: string | null): string => {
  if (!iso) {
    return MISSING;
  }
  // The single KST formatter, so this column cannot drift from every other date on the screens [T09].
  const day = kstDay(iso);
  return day === '—' ? MISSING : day;
};

const VIEW_ORDER: Array<Exclude<ReadyToOutboundTab, 'all'>> = ['inv', 'mkt', 'jit'];

/**
 * Inventory, then Marketing, then JIT — the order the tabs read in. The server tiers each page on its
 * own, so a Load more appended a second Inventory band under the first page's JIT rows [SSD-6939 19-8].
 * Stable, so each band keeps the server's order.
 */
export const groupedByView = (orders: ReadyToOutboundRow[]): ReadyToOutboundRow[] =>
  VIEW_ORDER.flatMap((view) => orders.filter((order) => viewOf(order) === view));

/** What a bulk outbound would take and what it would refuse, so Select all can say both [SSD-6939 19-3]. */
export interface OutboundableSplit {
  outboundable: number;
  blocked: number;
}

export const outboundableSplit = (orders: ReadyToOutboundRow[]): OutboundableSplit => {
  const blocked = orders.filter((order) => (order.violations ?? []).length > 0).length;
  return { outboundable: orders.length - blocked, blocked };
};

/** One shelf stop for one sku: what the picker pulls once, then splits across the orders beneath it [SSD-6923]. */
export interface PickTotal {
  location: string | null;
  sku: string;
  productName: string;
  isSample: boolean;
  qty: number;
  /** The per-order lines that make up the total, still in the server's order. */
  picks: PickListPick[];
}

/**
 * Folds the walk into one total per location and sku. Each total sits where its first line did, so the
 * server's serpentine route is kept; a sample never merges with merchandise that shares its sku.
 */
export const pickTotals = (picks: PickListPick[]): PickTotal[] => {
  const totals = new Map<string, PickTotal>();
  for (const pick of picks) {
    const isSample = pick.isSample === true;
    const key = JSON.stringify([pick.location, pick.sku, isSample]);
    const total = totals.get(key);
    if (total) {
      total.qty += pick.qty;
      total.picks.push(pick);
    } else {
      totals.set(key, {
        location: pick.location,
        sku: pick.sku,
        productName: pick.productName,
        isSample,
        qty: pick.qty,
        picks: [pick],
      });
    }
  }
  return [...totals.values()];
};
